// @ts-nocheck
import React from 'react'
import { filter, join, upperFirst } from 'lodash'
import { colors } from '@lmc/common/constants'
import {
  convertPluralQuantityToSingular,
  convertDoseNumberToWord
} from '../utils'
import { getFrequencyDescription } from '../../../fields/types/MedicationFrequencyField/utils'

interface EmarMedicationAllDetailsProps {
  displayTitle?: boolean
  fields: Record<string, any>
  formState: Record<string, any>
  testId?: string
}

const renderMedicationDetailsTitle = (props: EmarMedicationAllDetailsProps) => {
  const { displayTitle = true, testId } = props

  if (!displayTitle) return null

  return (
    <div
      style={styles.detailsTitleContainer}
      data-testid={
        testId ? `${testId}-details-title-container` : 'details-title-container'
      }
    >
      <h3 style={styles.detailsTitle}>Medication Details</h3>
      <div style={styles.detailsTitleUnderline} />
    </div>
  )
}

const renderDetail = (
  detail: any,
  comment: any,
  testId?: string,
  key?: string
) => {
  return (
    <div data-testid={testId ? `${testId}-detail-${key}` : `detail-${key}`}>
      <div
        data-testid={
          testId ? `${testId}-detail-value-${key}` : `detail-value-${key}`
        }
      >
        {detail}
      </div>
      {comment && (
        <div
          data-testid={
            testId ? `${testId}-detail-comment-${key}` : `detail-comment-${key}`
          }
        >
          {comment}
        </div>
      )}
    </div>
  )
}

const renderQuantityDetail = (props: EmarMedicationAllDetailsProps) => {
  const {
    fields: { quantity, quantityType, quantity_comment },
    testId
  } = props
  const detail = `${quantity} ${quantityType}`

  return renderDetail(detail, quantity_comment, testId, 'quantity')
}

const getFixedDoseDetail = (props: EmarMedicationAllDetailsProps) => {
  const {
    // @ts-ignore
    altDose,
    // @ts-ignore
    dose,
    fields: { quantityType, variableDose }
  } = props

  const doseWord = convertDoseNumberToWord(dose, quantityType)
  const altDoseWord = convertDoseNumberToWord(altDose, quantityType)
  const quantityTypeWord = quantityType
    ? !variableDose && dose === 1
      ? convertPluralQuantityToSingular(quantityType)
      : quantityType
    : ''

  return `${doseWord}${altDose ? ` or ${altDoseWord}` : ''} ${quantityTypeWord}`
}

const getNonFixedDoseDetail = (props: EmarMedicationAllDetailsProps) => {
  const { checkedEmarRoundsDetails, testId } = props

  return (
    <div data-testid={testId ? `${testId}-dose-details` : 'dose-details'}>
      {checkedEmarRoundsDetails.map((round, index) => {
        const doseText = getFixedDoseDetail({
          ...props,
          dose: round.dose,
          altDose: round.altDose
        })
        return (
          <div
            key={round.emarRoundTiming}
            data-testid={
              testId ? `${testId}-dose-round-${index}` : `dose-round-${index}`
            }
          >
            {`${doseText} at ${round.time}`}
          </div>
        )
      })}
    </div>
  )
}

const renderDoseDetail = (props: EmarMedicationAllDetailsProps) => {
  const {
    fields: { altDose, dose, emarRoundsDetails, PRN }
  } = props

  if (PRN) {
    if (dose) {
      return getFixedDoseDetail({ ...props, dose, altDose })
    }
    return getFixedDoseDetail({
      ...props,
      dose: emarRoundsDetails[0].dose,
      altDose: emarRoundsDetails[0].altDose
    })
  }

  const checkedEmarRoundsDetails =
    emarRoundsDetails[0].isChecked == null
      ? emarRoundsDetails
      : filter(emarRoundsDetails, 'isChecked')

  const doseText = getNonFixedDoseDetail({ ...props, checkedEmarRoundsDetails })

  return doseText
}

const renderHoursDetail = (props: EmarMedicationAllDetailsProps) => {
  const {
    fields: { emarRoundsDetails, PRN },
    testId
  } = props

  if (PRN) {
    return (
      <div data-testid={testId ? `${testId}-hours-prn` : 'hours-prn'}>PRN</div>
    )
  }

  const checkedEmarRoundsDetails =
    emarRoundsDetails[0].isChecked == null
      ? emarRoundsDetails
      : filter(emarRoundsDetails, 'isChecked')

  const times = checkedEmarRoundsDetails.map((round) => round.time)

  return (
    <div data-testid={testId ? `${testId}-hours-times` : 'hours-times'}>
      {join(times, ', ')}
    </div>
  )
}

const renderEndDateDetail = (props: EmarMedicationAllDetailsProps) => {
  const {
    fields: { endDate, hasEndDate_comment },
    testId
  } = props
  const detail = endDate || 'Ongoing'

  return renderDetail(detail, hasEndDate_comment, testId, 'endDate')
}

const renderFrequencyDetail = (props: EmarMedicationAllDetailsProps) => {
  const {
    fields,
    fields: { freqInterval },
    testId
  } = props

  if (!freqInterval) return null

  return (
    <div
      data-testid={testId ? `${testId}-frequency-detail` : 'frequency-detail'}
    >
      {upperFirst(
        getFrequencyDescription({ ...fields, freqInterval: `${freqInterval}` })
      )}
    </div>
  )
}

const renderSpecialDetail = (props: EmarMedicationAllDetailsProps) => {
  const { key } = props

  switch (key) {
    case 'quantity':
      return renderQuantityDetail(props)
    case 'dose':
      return renderDoseDetail(props)
    case 'hours':
      return renderHoursDetail(props)
    case 'endDate':
      return renderEndDateDetail(props)
    case 'freqInterval':
      return renderFrequencyDetail(props)
    default:
      return null
  }
}

const renderDetailRowInfo = (props: EmarMedicationAllDetailsProps) => {
  const { commentKey, fields, key, testId } = props

  const specialKeys = ['quantity', 'dose', 'hours', 'endDate', 'freqInterval']

  if (specialKeys.includes(key)) {
    return renderSpecialDetail(props)
  }
  if (typeof fields[key] === 'boolean') {
    const detail = fields[key] ? 'Yes' : 'No'
    const comment = commentKey && fields[commentKey]

    return renderDetail(detail, comment, testId, key)
  }

  return (
    <div
      data-testid={
        testId ? `${testId}-detail-value-${key}` : `detail-value-${key}`
      }
    >
      {fields[key]}
    </div>
  )
}

const renderMedicationDetailsList = (props: EmarMedicationAllDetailsProps) => {
  const { fields, testId } = props

  const medDetailsOrder = [
    { key: 'PRN', title: 'PRN' },
    { key: 'dose', title: 'Dose' },
    { key: 'hours', title: 'Hours' },
    { key: 'startDate', title: 'Start Date' },
    { key: 'freqInterval', title: 'Frequency' },
    { key: 'endDate', title: 'End Date' },
    { key: 'cautionaryInstructions', title: 'Cautionary Instructions' },
    { key: 'route', title: 'Route' },
    {
      key: 'secondSignatureRequired',
      title: 'Requires second signature',
      commentKey: 'secondSignatureRequired_comment'
    },
    {
      key: 'homeRemedy',
      title: 'Homely Remedy',
      commentKey: 'homeRemedy_comment'
    },
    {
      key: 'overTheCounter',
      title: 'Over the Counter',
      commentKey: 'overTheCounter_comment'
    },
    {
      key: 'blisterPackName',
      title: 'Part of a pharmacy filled compliance aid'
    },
    {
      key: 'controlledDrug',
      title: 'Controlled Drug',
      commentKey: 'controlledDrug_comment'
    },
    { key: 'description', title: 'Description' },
    { key: 'additionalSupport', title: 'Additional Support' },
    { key: 'quantity', title: 'Stock Quantity' }
  ]

  return (
    <div
      style={styles.detailsListContainer}
      data-testid={testId ? `${testId}-details-list` : 'details-list'}
    >
      {medDetailsOrder.map((entry) => {
        if (
          (fields[entry.key] == null || fields[entry.key] === '') &&
          entry.key !== 'dose' &&
          entry.key !== 'hours' &&
          entry.key !== 'endDate'
        )
          return null

        return (
          <div
            style={styles.detailContainer}
            key={entry.key}
            data-testid={
              testId
                ? `${testId}-detail-row-${entry.key}`
                : `detail-row-${entry.key}`
            }
          >
            <div
              style={styles.detailTitle}
              data-testid={
                testId
                  ? `${testId}-detail-title-${entry.key}`
                  : `detail-title-${entry.key}`
              }
            >
              {entry.title}
            </div>
            <div
              style={styles.detailInfo}
              data-testid={
                testId
                  ? `${testId}-detail-info-${entry.key}`
                  : `detail-info-${entry.key}`
              }
            >
              {renderDetailRowInfo({
                ...props,
                key: entry.key,
                commentKey: entry.commentKey
              })}
            </div>
          </div>
        )
      })}
    </div>
  )
}

const EmarMedicationAllDetails: React.FC<EmarMedicationAllDetailsProps> = (
  props
) => {
  const { testId } = props
  return (
    <div data-testid={testId ? `${testId}-all-details` : 'all-details'}>
      {renderMedicationDetailsTitle(props)}
      {renderMedicationDetailsList(props)}
    </div>
  )
}

const styles = {
  detailContainer: {
    borderBottom: `1px solid ${colors.grey}`,
    padding: '7px 0px'
  },
  detailInfo: {
    lineHeight: '22px'
  },
  detailTitle: {
    fontWeight: 700,
    lineHeight: '22px'
  },
  detailsContainer: {
    marginBottom: 32,
    width: '100%'
  },
  detailsListContainer: {
    background: colors.white,
    borderRadius: 15,
    padding: '5px 10px 12px'
  },
  detailsTitle: {
    fontWeight: 400,
    fontSize: 20,
    lineHeight: '30px',
    marginBottom: 3
  },
  detailsTitleContainer: {
    marginBottom: 16
  },
  detailsTitleUnderline: {
    border: `1px solid ${colors.grey}`
  }
}
export default EmarMedicationAllDetails
